import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import { StatusBadge } from "@/components/Shared";
import DataTable from "@/components/DataTable";
import { InlineEditSelect } from "@/components/InlineEdit";
import SignatureCanvas from "@/components/SignatureCanvas";
import HealthBadge, { timeEntryHealth } from "@/components/HealthBadge";
import DepartmentFilter, { useDepartmentFilter } from "@/components/DepartmentFilter";
import FirmFilter from "@/components/FirmFilter";
import EmployeePicker from "@/components/EmployeePicker";
import VehiclePicker from "@/components/VehiclePicker";
import EmployeeStatusFilter from "@/components/EmployeeStatusFilter";
import MassDeleteToolbar from "@/components/MassDeleteToolbar";
import ZeitMassActionToolbar from "@/components/ZeitMassActionToolbar";
import { Plus, X, Pencil, Trash2, Copy, ClipboardList, CheckCircle2, XCircle, Smartphone, Upload, Calendar, ChevronLeft, ChevronRight, Download, AlertTriangle, Gauge, FileSpreadsheet, Bot } from "lucide-react";
import { toast } from "sonner";
import ZeiterfassungBulkImportModal from "@/components/ZeiterfassungBulkImportModal";
import ModuleHint from "@/components/ModuleHint";

const EMPTY = {
  date: new Date().toISOString().slice(0,10), employee_id: "", vehicle_id: "",
  workplace: "", work_start: "06:00", work_end: "14:30", break_minutes: 0,
  driving_start: "", driving_end: "", start_km: 0, end_km: 0, status: "offen", notes: "",
  is_copilot: false, start_address: "", end_address: "", breaks: [],
  signature_data_url: "", anmerkung: "",
};

export default function Zeiterfassung() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin" || user?.is_super_admin;
  const { can } = usePermissions();
  const canCreate = can("operations", "create");
  const canEdit = can("operations", "edit");
  const canDelete = can("operations", "delete");
  const canApprove = can("operations", "approve");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [empFilter, setEmpFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [portalOnly, setPortalOnly] = useState(false);
  const [autoOnly, setAutoOnly] = useState(false);
  const [deptFilter, setDeptFilter] = useDepartmentFilter("zeiterfassung");
  const [empStatus, setEmpStatus] = useState("aktiv");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [missingToday, setMissingToday] = useState([]);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sp, setSp] = useSearchParams();
  // Phase 5Z.33 — additional client-side quick filters (Pills)
  const [pillFilter, setPillFilter] = useState(""); // "with_damage" | "with_driving" | "today" | ""
  const [exporting, setExporting] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);

  async function fetchDefaults(eid, targetDate) {
    if (!eid) { setDefaults(null); return; }
    try {
      const { data } = await api.get(`/employees/${eid}/defaults`, { params: { target_date: targetDate || "" } });
      setDefaults(data);
      // Apply auto-fill ONLY when creating new (not editing) AND the form fields are still empty
      // Skip auto-fill for non-working shifts (frei/urlaub/krank/homeoffice)
      const NON_WORKING = ["frei", "urlaub", "krank", "homeoffice"];
      const isWorkingShift = !data.shift_today || !NON_WORKING.includes(data.shift_today.shift_type);
      if (!editing) {
        setForm((f) => {
          const upd = { ...f };
          if (data.last_vehicle_id && !upd.vehicle_id) upd.vehicle_id = data.last_vehicle_id;
          if (data.suggested_start_km && (!upd.start_km || upd.start_km === 0)) {
            upd.start_km = data.suggested_start_km;
          }
          if (isWorkingShift && data.shift_today) {
            if (!upd.workplace && data.shift_today.workplace) upd.workplace = data.shift_today.workplace;
            if (data.shift_today.start_time && !upd.work_start) upd.work_start = data.shift_today.start_time;
            if (data.shift_today.end_time && !upd.work_end) upd.work_end = data.shift_today.end_time;
          }
          return upd;
        });
      }
    } catch { setDefaults(null); }
  }

  async function load() {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, employee_id: empFilter, status: statusFilter };
      if (deptFilter) params.department_id = deptFilter;
      if (empStatus) params.employee_status = empStatus;
      const { data } = await api.get("/time-entries", { params });
      setItems(portalOnly ? (data || []).filter((t) => t.submitted_via_portal) : data);
    } finally { setLoading(false); }
  }
  useEffect(() => {
    api.get("/employees").then(({data}) => setEmployees(data));
    api.get("/vehicles").then(({data}) => setVehicles(data));
    api.get("/operations/missing-time-entries")
      .then(({data}) => setMissingToday(data.missing || []))
      .catch(() => setMissingToday([]));
  }, []);
  // load() is intentionally stable — recreating the closure on each filter
  // change would re-trigger this effect, doubling fetches. The eslint-disable
  // is the canonical solution for this exact filter-driven-fetch pattern.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dateFrom, dateTo, empFilter, statusFilter, deptFilter, empStatus, portalOnly]);

  // ?new=1 support + Phase 5Z.12 ?status=...&portal=1 deep-link
  useEffect(() => {
    if (sp.get("new") === "1") {
      openCreate();
      sp.delete("new"); setSp(sp, { replace: true });
    }
    const st = sp.get("status");
    if (st && st !== statusFilter) setStatusFilter(st);
    if (sp.get("portal") === "1" && !portalOnly) setPortalOnly(true);
  }, [sp, setSp]); // eslint-disable-line

  const empName = (id) => { const e = employees.find(x => x.id === id); return e ? `${e.first_name} ${e.last_name}` : "—"; };
  const vehPlate = (id) => { const v = vehicles.find(x => x.id === id); return v ? v.license_plate : "—"; };

  // Phase 5Z.33 — Status-Summary + Pill-Filter + Quick-Export
  const today_iso = new Date().toISOString().slice(0, 10);
  const visibleItems = useMemo(() => {
    let xs = items;
    if (autoOnly) xs = xs.filter((t) => t.auto_generated);
    if (!pillFilter) return xs;
    if (pillFilter === "with_damage") return xs.filter((t) => t.damage_reported || t.has_damage || (t.notes || "").toLowerCase().includes("schaden"));
    if (pillFilter === "with_driving") return xs.filter((t) => !!(t.driving_start && t.driving_end));
    if (pillFilter === "today") return xs.filter((t) => t.date === today_iso);
    return xs;
  }, [items, pillFilter, today_iso, autoOnly]);

  const statusCounts = useMemo(() => {
    const c = { offen: 0, geprueft: 0, freigegeben: 0, abgelehnt: 0, total: items.length };
    for (const t of items) {
      const s = t.status || "offen";
      if (s in c) c[s] += 1;
    }
    return c;
  }, [items]);

  async function exportCurrentToExcel() {
    setExporting(true);
    try {
      // Client-side CSV of current visible/filtered view — robust, no backend
      // contract dependency (Excel-compatible UTF-8 BOM).
      const cols = [
        ["date", "Datum"], ["employee", "Mitarbeiter"], ["vehicle", "Fahrzeug"],
        ["work_start", "Beginn"], ["work_end", "Ende"], ["break_minutes", "Pause (min)"],
        ["work_minutes", "Arbeit (min)"], ["driving_start", "Lenkbeginn"], ["driving_end", "Lenkende"],
        ["start_km", "Start KM"], ["end_km", "End KM"], ["total_km", "KM"],
        ["workplace", "Einsatzort"], ["status", "Status"], ["anmerkung", "Anmerkung"],
      ];
      const escape = (v) => {
        if (v === null || v === undefined) return "";
        const s = String(v).replace(/"/g, '""');
        return /[",;\n]/.test(s) ? `"${s}"` : s;
      };
      const lines = [cols.map(([, l]) => l).join(";")];
      for (const t of visibleItems) {
        const row = cols.map(([k]) => {
          if (k === "employee") return empName(t.employee_id);
          if (k === "vehicle") return vehPlate(t.vehicle_id);
          if (k === "total_km") return Math.max(0, (t.end_km || 0) - (t.start_km || 0));
          if (k === "work_minutes") {
            if (!t.work_start || !t.work_end) return "";
            const [sh, sm] = t.work_start.split(":").map(Number);
            const [eh, em] = t.work_end.split(":").map(Number);
            let mins = (eh * 60 + em) - (sh * 60 + sm);
            if (mins < 0) mins += 24 * 60;
            return Math.max(0, mins - (t.break_minutes || 0));
          }
          return t[k] ?? "";
        }).map(escape);
        lines.push(row.join(";"));
      }
      const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `Zeiterfassung_${stamp}_${visibleItems.length}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${visibleItems.length} Einträge exportiert (CSV)`);
    } catch (e) {
      toast.error("Export fehlgeschlagen");
    } finally { setExporting(false); }
  }

  async function bulkApproveAllVisible() {
    const openOnes = visibleItems.filter((t) => t.status === "offen" || t.status === "geprueft");
    if (openOnes.length === 0) { toast.info("Keine offenen oder geprüften Einträge in der aktuellen Auswahl."); return; }
    if (!window.confirm(`${openOnes.length} sichtbare(n) Eintrag/Einträge freigeben?`)) return;
    setBulkApproving(true);
    try {
      const { data } = await api.post("/time-entries/bulk-approve", { ids: openOnes.map((t) => t.id) });
      toast.success(`${data.modified} freigegeben`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Massen-Freigabe fehlgeschlagen"); }
    finally { setBulkApproving(false); }
  }

  async function save() {
    if (!form.employee_id) { toast.error("Mitarbeiter ist Pflichtfeld"); return; }
    if (form.vehicle_id && (!form.start_km || !form.end_km)) {
      if (!window.confirm("Fahrzeug ist zugewiesen, aber Start-/End-KM fehlen. Trotzdem speichern?")) return;
    }
    setSaving(true);
    try {
      const payload = { ...form, break_minutes: +form.break_minutes || 0, start_km: +form.start_km || 0, end_km: +form.end_km || 0 };
      if (editing) await api.put(`/time-entries/${editing.id}`, payload);
      else await api.post("/time-entries", payload);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setSaving(false); }
  }
  async function remove(id, dat) {
    if (!window.confirm(`Zeiteintrag vom ${dat} löschen?`)) return;
    try { await api.delete(`/time-entries/${id}`); toast.success("Gelöscht"); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  }
  async function duplicate(t) {
    const tomorrow = new Date(t.date + "T00:00:00");
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newDate = tomorrow.toISOString().slice(0, 10);
    const dup = {
      ...t, date: newDate, status: "offen",
      // remove server-computed fields and id
    };
    delete dup.id; delete dup.created_at; delete dup.updated_at;
    delete dup.work_minutes; delete dup.driving_minutes; delete dup.total_km;
    setEditing(null); setForm({ ...EMPTY, ...dup }); setDefaults(null); setShowForm(true);
    toast.info(`Eintrag dupliziert für ${newDate}`);
  }
  async function inlineStatus(id, newStatus) {
    await api.put(`/time-entries/${id}`, { status: newStatus });
    setItems((cur) => cur.map((x) => x.id === id ? { ...x, status: newStatus } : x));
    toast.success(`Status: ${newStatus}`);
  }
  async function approveEntry(t) {
    if (!window.confirm(`Zeiteintrag vom ${t.date} freigeben?`)) return;
    try {
      await api.post(`/time-entries/${t.id}/approve`);
      toast.success("Eintrag freigegeben");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Freigabe fehlgeschlagen"); }
  }
  async function rejectEntry(t) {
    const reason = window.prompt(`Zeiteintrag vom ${t.date} ablehnen?\n\nGrund (optional):`);
    if (reason === null) return; // user cancelled
    try {
      await api.post(`/time-entries/${t.id}/reject`, { reason });
      toast.success("Eintrag abgelehnt");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Ablehnung fehlgeschlagen"); }
  }
  function openCreate() {
    setEditing(null); setForm(EMPTY); setDefaults(null); setShowForm(true);
  }
  function openEdit(t) {
    setEditing(t); setForm({...EMPTY,...t}); setDefaults(null); setShowForm(true);
  }
  function closeForm() {
    setShowForm(false); setEditing(null); setForm(EMPTY); setDefaults(null);
  }
  function missing(t) { return !t.work_start || !t.work_end || !t.employee_id; }

  function preFillForEmployee(emp) {
    setEditing(null);
    setForm({ ...EMPTY, employee_id: emp.employee_id });
    setDefaults(null);
    setShowForm(true);
    fetchDefaults(emp.employee_id, new Date().toISOString().slice(0, 10));
  }

  return (
    <div className="p-6 md:p-8" data-testid="zeiterfassung-page">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100">Zeiterfassung</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Arbeits- und Lenkzeiten pro Tag und Mitarbeiter</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="bulk-import-zeit-btn" onClick={() => setShowBulkImport(true)} disabled={!canCreate}
            className="inline-flex items-center gap-2 bg-emerald-600/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/25 text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Excel-Bulk-Import für einen Fahrer & einen ganzen Monat">
            <Upload className="w-4 h-4" /> Bulk-Import
          </button>
          <button data-testid="add-time-btn" onClick={openCreate} disabled={!canCreate}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-md">
            <Plus className="w-4 h-4" /> Zeiteintrag
          </button>
        </div>
      </div>

      <ZeiterfassungBulkImportModal
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onDone={() => load()}
      />

      <ModuleHint moduleKey="zeiterfassung" />

      {missingToday.length > 0 && (
        <div className="mb-4 p-4 bg-amber-500/5 border border-amber-500/30 rounded-md" data-testid="missing-time-alert">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-amber-400 text-sm font-semibold">⚠️ Heute eingeplant, aber noch kein Zeiteintrag erfasst ({missingToday.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {missingToday.map((m) => (
              <button
                key={m.shift_id}
                onClick={() => preFillForEmployee(m)}
                data-testid={`missing-time-emp-${m.employee_id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 text-zinc-200 rounded transition-colors"
                title={`${m.shift_type || ""} ${m.shift_start || ""} – ${m.shift_end || ""} ${m.workplace || ""}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                <span className="font-medium">{m.name}</span>
                {m.shift_start && <span className="text-zinc-500 font-mono">{m.shift_start}–{m.shift_end}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Phase 5Z.33 — Status-Summary-Strip + Quick-Filter-Pills + Quick-Export */}
      {items.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="zeit-status-strip">
          <StatusPill label="Total" count={statusCounts.total} active={statusFilter === ""} onClick={() => setStatusFilter("")} testid="zeit-strip-total" />
          <StatusPill label="Offen" count={statusCounts.offen} color="amber" active={statusFilter === "offen"} onClick={() => setStatusFilter(statusFilter === "offen" ? "" : "offen")} testid="zeit-strip-offen" />
          <StatusPill label="Geprüft" count={statusCounts.geprueft} color="blue" active={statusFilter === "geprueft"} onClick={() => setStatusFilter(statusFilter === "geprueft" ? "" : "geprueft")} testid="zeit-strip-geprueft" />
          <StatusPill label="Freigegeben" count={statusCounts.freigegeben} color="emerald" active={statusFilter === "freigegeben"} onClick={() => setStatusFilter(statusFilter === "freigegeben" ? "" : "freigegeben")} testid="zeit-strip-freigegeben" />
          {statusCounts.abgelehnt > 0 && (
            <StatusPill label="Abgelehnt" count={statusCounts.abgelehnt} color="rose" active={statusFilter === "abgelehnt"} onClick={() => setStatusFilter(statusFilter === "abgelehnt" ? "" : "abgelehnt")} testid="zeit-strip-abgelehnt" />
          )}
          <span className="mx-1 w-px h-5 bg-zinc-800" />
          <PillBtn icon={Calendar} label="Heute" active={pillFilter === "today"} onClick={() => setPillFilter(pillFilter === "today" ? "" : "today")} testid="zeit-pill-today" />
          <PillBtn icon={Gauge} label="Mit Lenkzeit" active={pillFilter === "with_driving"} onClick={() => setPillFilter(pillFilter === "with_driving" ? "" : "with_driving")} testid="zeit-pill-driving" />
          <PillBtn icon={AlertTriangle} label="Mit Schaden" active={pillFilter === "with_damage"} onClick={() => setPillFilter(pillFilter === "with_damage" ? "" : "with_damage")} testid="zeit-pill-damage" />
          {pillFilter && (
            <button onClick={() => setPillFilter("")} className="text-[11px] text-zinc-500 hover:text-zinc-300" data-testid="zeit-pill-clear">Filter leeren</button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {canApprove && (statusFilter === "offen" || statusFilter === "geprueft") && visibleItems.length > 0 && (
              <button
                onClick={bulkApproveAllVisible}
                disabled={bulkApproving}
                data-testid="zeit-bulk-approve-visible"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded font-medium"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {bulkApproving ? "..." : `${visibleItems.filter(t => t.status === "offen" || t.status === "geprueft").length} sichtbare freigeben`}
              </button>
            )}
            <button
              onClick={exportCurrentToExcel}
              disabled={exporting || visibleItems.length === 0}
              data-testid="zeit-quick-export"
              title="Aktuelle Auswahl als CSV exportieren (Excel-kompatibel)"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-200 border border-zinc-700 rounded"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              {exporting ? "..." : "CSV"}
            </button>
          </div>
        </div>
      )}

      <DataTable
        rows={visibleItems}
        loading={loading}
        searchableFields={[
          "workplace", "notes", "anmerkung", "start_address", "end_address", "date",
          (r) => empName(r.employee_id),
          (r) => vehPlate(r.vehicle_id),
        ]}
        searchPlaceholder="Mitarbeiter, Kennzeichen, Einsatzort, Notiz..."
        testid="zeiterfassung-table"
        emptyTitle="Keine Zeiteinträge"
        emptyHint={dateFrom || empFilter || statusFilter || pillFilter ? "Versuchen Sie andere Filter." : "Erstellen Sie den ersten Eintrag."}
        rowClassName={(t) => missing(t) ? "bg-red-500/5" : ""}
        filters={
          <>
            <FirmFilter compact />
            <DepartmentFilter value={deptFilter} onChange={setDeptFilter} module="zeiterfassung" testid="zeiterfassung-dept-filter"/>
            <EmployeeStatusFilter value={empStatus} onChange={setEmpStatus} testid="zeiterfassung-empstatus-filter"/>
            <DayPicker dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} />
            <input type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} data-testid="time-date-from"
              id="zeit-date-from"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            <input type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} data-testid="time-date-to"
              id="zeit-date-to"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            <DatePresetBar setDateFrom={setDateFrom} setDateTo={setDateTo} />
            <div className="min-w-[200px]" data-testid="time-emp-filter-wrap">
              <EmployeePicker
                value={empFilter}
                onChange={setEmpFilter}
                placeholder="Alle Mitarbeiter"
                testid="time-emp-filter"
              />
            </div>
            <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} data-testid="time-status-filter"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
              <option value="">Alle Status</option>
              <option value="offen">Offen</option><option value="geprueft">Geprüft</option><option value="freigegeben">Freigegeben</option><option value="abgelehnt">Abgelehnt</option>
            </select>
            <label className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-300 cursor-pointer select-none" data-testid="time-portal-only-wrap">
              <input type="checkbox" checked={portalOnly} onChange={(e) => setPortalOnly(e.target.checked)} data-testid="time-portal-only" className="accent-amber-500" />
              <span>Nur Portal</span>
            </label>
            <label className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-300 cursor-pointer select-none" data-testid="time-auto-only-wrap">
              <input type="checkbox" checked={autoOnly} onChange={(e) => setAutoOnly(e.target.checked)} data-testid="time-auto-only" className="accent-violet-500" />
              <span>Nur Auto-generiert</span>
            </label>
          </>
        }
        columns={[
          ...((isAdmin || canApprove || canDelete) ? [{
            key: "_select",
            label: (
              <input
                type="checkbox"
                title="Alle auf dieser Seite auswählen"
                checked={items.length > 0 && items.every(t => selectedIds.has(t.id))}
                onChange={(e) => {
                  if (e.target.checked) setSelectedIds(new Set(items.map(t => t.id)));
                  else setSelectedIds(new Set());
                }}
                data-testid="time-select-all"
                className="w-3.5 h-3.5 rounded accent-rose-500 cursor-pointer"
              />
            ),
            headerClassName: "w-6",
            render: (t) => (
              <input
                type="checkbox"
                checked={selectedIds.has(t.id)}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  setSelectedIds(prev => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(t.id); else next.delete(t.id);
                    return next;
                  });
                }}
                data-testid={`time-select-${t.id}`}
                className="w-3.5 h-3.5 rounded accent-rose-500 cursor-pointer"
              />
            ),
          }] : []),
          { key: "_health", label: "",
            headerClassName: "w-6",
            render: (t) => {
              const h = timeEntryHealth(t);
              return <HealthBadge level={h.level} reasons={h.reasons} variant="dot" size="md" testid={`time-health-${t.id}`} />;
            } },
          { key: "date", label: "Datum", sortable: true, render: (t) => (
            <span className="font-mono text-xs text-zinc-300 inline-flex items-center gap-1.5">
              {t.date}
              {t.submitted_via_portal && (
                <span title="Eingereicht über Fahrer-Portal" data-testid={`time-portal-${t.id}`} className="inline-flex items-center px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[9px] font-semibold leading-none uppercase tracking-wider">
                  <Smartphone className="w-2.5 h-2.5 mr-0.5" /> Portal
                </span>
              )}
              {t.auto_generated && (
                <span
                  title={`Auto-generiert${t.auto_gen_rule_code ? ` (Regel: ${t.auto_gen_rule_code})` : ""}${t.auto_gen_run_id ? ` · Run ${String(t.auto_gen_run_id).slice(0, 8)}` : ""}`}
                  data-testid={`time-auto-${t.id}`}
                  className="inline-flex items-center px-1 py-0.5 rounded bg-violet-500/15 text-violet-300 text-[9px] font-semibold leading-none uppercase tracking-wider"
                >
                  <Bot className="w-2.5 h-2.5 mr-0.5" /> Auto
                </span>
              )}
            </span>
          ) },
          { key: "_emp", label: "Mitarbeiter", render: (t) => <span className="text-zinc-100">{empName(t.employee_id)}</span> },
          { key: "_veh", label: "Fahrzeug", render: (t) => <span className="font-mono text-xs text-zinc-400">{vehPlate(t.vehicle_id)}</span> },
          { key: "_period", label: "Arbeitszeiten", render: (t) => <span className="font-mono text-xs text-zinc-400">{t.work_start || "—"} – {t.work_end || "—"}</span> },
          { key: "work_minutes", label: "Arbeit", sortable: true,
            headerClassName: "text-right", className: "text-right font-mono tabular-nums text-zinc-300",
            render: (t) => {
              // Compute on the fly from work_start/work_end – work_minutes
              // is not stored on the entry; Kodlogy-parity = net (minus break).
              if (!t.work_start || !t.work_end) return "—";
              const [sh, sm] = t.work_start.split(":").map(Number);
              const [eh, em] = t.work_end.split(":").map(Number);
              let mins = (eh * 60 + em) - (sh * 60 + sm);
              if (mins < 0) mins += 24 * 60;
              mins = Math.max(0, mins - (t.break_minutes || 0));
              return `${Math.floor(mins / 60)}h ${mins % 60}m`;
            } },
          { key: "break_minutes", label: "Pause", sortable: true,
            headerClassName: "text-right", className: "text-right font-mono tabular-nums text-zinc-400",
            render: (t) => `${t.break_minutes||0}m` },
          { key: "total_km", label: "KM", sortable: true,
            headerClassName: "text-right", className: "text-right font-mono tabular-nums text-zinc-300",
            render: (t) => Math.max(0, (t.end_km || 0) - (t.start_km || 0)) },
          { key: "status", label: "Status", sortable: true, render: (t) => (
            <InlineEditSelect
              value={t.status}
              options={[["offen","Offen"],["geprueft","Geprüft"],["freigegeben","Freigegeben"],["abgelehnt","Abgelehnt"]]}
              onSave={(v) => inlineStatus(t.id, v)}
              render={(v) => <StatusBadge status={v}/>}
              testid={`time-status-${t.id}`}
              disabled={!canEdit}
            />
          ) },
          {
            key: "_actions", label: "",
            headerClassName: "text-right", className: "text-right",
            render: (t) => (
              <>
                {canApprove && t.status !== "freigegeben" && (
                  <button onClick={(e) => { e.stopPropagation(); approveEntry(t); }} title="Freigeben"
                          data-testid={`time-approve-${t.id}`}
                          className="p-1.5 text-zinc-400 hover:text-emerald-400">
                    <CheckCircle2 className="w-3.5 h-3.5"/>
                  </button>
                )}
                {canApprove && t.status !== "abgelehnt" && (
                  <button onClick={(e) => { e.stopPropagation(); rejectEntry(t); }} title="Ablehnen"
                          data-testid={`time-reject-${t.id}`}
                          className="p-1.5 text-zinc-400 hover:text-red-400 ml-1">
                    <XCircle className="w-3.5 h-3.5"/>
                  </button>
                )}
                {canCreate && <button onClick={(e) => { e.stopPropagation(); duplicate(t); }} title="Duplizieren (nächster Tag)" data-testid={`time-dup-${t.id}`} className="p-1.5 text-zinc-400 hover:text-emerald-400 ml-1"><Copy className="w-3.5 h-3.5"/></button>}
                {canEdit && <button onClick={(e) => { e.stopPropagation(); openEdit(t); }} title="Bearbeiten" className="p-1.5 text-zinc-400 hover:text-blue-400 ml-1"><Pencil className="w-3.5 h-3.5"/></button>}
                {canDelete && <button onClick={(e) => { e.stopPropagation(); remove(t.id, t.date); }} title="Löschen" data-testid={`time-del-${t.id}`} className="p-1.5 text-zinc-400 hover:text-red-400 ml-1"><Trash2 className="w-3.5 h-3.5"/></button>}
                {!canCreate && !canEdit && !canDelete && !canApprove && <span className="text-zinc-600 text-xs">—</span>}
              </>
            ),
          },
        ]}
      />

      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-4xl max-h-[92vh] overflow-y-auto">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
              <h3 className="font-display font-semibold text-zinc-50">{editing ? "Zeiteintrag bearbeiten" : "Neuer Zeiteintrag"}</h3>
              <button onClick={closeForm} className="text-zinc-400" data-testid="time-form-close"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              {defaults && form.employee_id && !editing && (
                <div className="col-span-2 -mb-2 p-3 bg-zinc-950 border border-zinc-800 rounded text-xs space-y-1.5" data-testid="time-defaults-hint">
                  {defaults.has_time_today && (
                    <div className="text-amber-400 flex items-center gap-1.5">⚠️ Heute existiert bereits ein Zeiteintrag für diesen Mitarbeiter.</div>
                  )}
                  {defaults.scheduled_today && defaults.shift_today && (() => {
                    const st = defaults.shift_today.shift_type;
                    const isOff = ["frei", "urlaub", "krank", "homeoffice"].includes(st);
                    return (
                      <div className={`flex items-center gap-1.5 ${isOff ? "text-amber-400" : "text-emerald-400"}`}>
                        {isOff ? "ℹ️" : "✓"} Heute geplant: <span className="font-medium capitalize">{st || "Schicht"}</span>
                        {!isOff && defaults.shift_today.start_time && <> {defaults.shift_today.start_time}–{defaults.shift_today.end_time}</>}
                        {defaults.shift_today.workplace && <> · {defaults.shift_today.workplace}</>}
                        {isOff && <span className="text-zinc-500">— normalerweise kein Zeiteintrag</span>}
                      </div>
                    );
                  })()}
                </div>
              )}
              {/* Kopf: Datum + MA + Beifahrer */}
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Datum</label>
                <input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Mitarbeiter *</label>
                <div className="mt-1">
                  <EmployeePicker value={form.employee_id} testid="time-emp-picker"
                    onChange={(v) => { setForm({ ...form, employee_id: v }); fetchDefaults(v, form.date); }} />
                </div></div>
              <div className="col-span-2 flex items-center gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.is_copilot}
                    onChange={(e)=>setForm({...form, is_copilot: e.target.checked})}
                    data-testid="time-copilot"
                    className="rounded accent-blue-600"/>
                  <span className="text-xs text-zinc-300">Beifahrer?</span>
                </label>
                <span className="text-[10px] text-zinc-500 italic">Wenn aktiv: dieser Mitarbeiter begleitet nur (kein eigenes Lenken/KM relevant)</span>
              </div>
              {/* Arbeits-Block */}
              <div className="col-span-2 border-t border-zinc-800 pt-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Arbeit</div>
              </div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Arbeitsbeginn</label>
                <input type="time" value={form.work_start} onChange={(e)=>setForm({...form, work_start:e.target.value})} data-testid="time-work-start" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Arbeitsende</label>
                <input type="time" value={form.work_end} onChange={(e)=>setForm({...form, work_end:e.target.value})} data-testid="time-work-end" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              <div className="col-span-2"><label className="text-[10px] uppercase tracking-wider text-zinc-500">Einsatzort</label>
                <input value={form.workplace} onChange={(e)=>setForm({...form, workplace:e.target.value})} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Startadresse</label>
                <input value={form.start_address} onChange={(e)=>setForm({...form, start_address:e.target.value})} data-testid="time-start-address" placeholder="Adresse auswählen" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Endadresse</label>
                <input value={form.end_address} onChange={(e)=>setForm({...form, end_address:e.target.value})} data-testid="time-end-address" placeholder="Adresse auswählen" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              {/* Fahrzeug-Block */}
              <div className="col-span-2 border-t border-zinc-800 pt-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Fahrzeug &amp; Lenkzeit</div>
              </div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Fahrzeug</label>
                <div className="mt-1">
                  <VehiclePicker value={form.vehicle_id} testid="time-veh-picker"
                    onChange={(v) => setForm({ ...form, vehicle_id: v })} />
                </div></div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Status</label>
                <select value={form.status} onChange={(e)=>setForm({...form, status:e.target.value})} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1">
                  {["offen","geprueft","freigegeben"].map(s=><option key={s} value={s}>{s}</option>)}
                </select></div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Start KM</label>
                <input type="number" value={form.start_km} onChange={(e)=>setForm({...form, start_km:e.target.value})} data-testid="time-start-km" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">End KM</label>
                <input type="number" value={form.end_km} onChange={(e)=>setForm({...form, end_km:e.target.value})} data-testid="time-end-km" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Lenkbeginn</label>
                <input type="time" value={form.driving_start} onChange={(e)=>setForm({...form, driving_start:e.target.value})} data-testid="time-driving-start" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              <div><label className="text-[10px] uppercase tracking-wider text-zinc-500">Lenkende</label>
                <input type="time" value={form.driving_end} onChange={(e)=>setForm({...form, driving_end:e.target.value})} data-testid="time-driving-end" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              {form.driving_start && form.driving_end && (
                <div className="col-span-2 -mt-1">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2.5 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded" data-testid="lenkprotokoll-indicator">
                    <ClipboardList className="w-3 h-3" />
                    Dieser Eintrag wird automatisch im Lenkprotokoll erscheinen
                  </span>
                </div>
              )}
              {/* Pausen-Block — visueller LP/RP/SO-Editor mit Live-Lenkzeit */}
              <div className="col-span-2 border-t border-zinc-800 pt-3">
                <BreakEditor
                  breaks={form.breaks || []}
                  onChange={(next) => setForm({ ...form, breaks: next })}
                  workStart={form.work_start}
                  workEnd={form.work_end}
                />
              </div>
              {/* Anmerkung + Signature */}
              <div className="col-span-2"><label className="text-[10px] uppercase tracking-wider text-zinc-500">Anmerkung</label>
                <textarea rows={2} value={form.anmerkung} onChange={(e)=>setForm({...form, anmerkung:e.target.value})} data-testid="time-anmerkung" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1"/></div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Unterschrift</label>
                <div className="mt-1">
                  <SignatureCanvas value={form.signature_data_url} onChange={(url) => setForm({ ...form, signature_data_url: url })} testid="time-signature"/>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2 sticky bottom-0 bg-zinc-900">
              <button onClick={closeForm} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="time-save-btn" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium">{saving ? "Speichere…" : "Speichern"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin mass-delete toolbar for Zeiterfassung */}
      <MassDeleteToolbar
        user={user}
        collection="time_entries"
        label="Zeiteinträge"
        selectedIds={Array.from(selectedIds)}
        onClearSelection={() => setSelectedIds(new Set())}
        onAfterDelete={() => { setSelectedIds(new Set()); load(); }}
      />

      {/* Phase 5Z.19k — Mass approve/reject/status toolbar */}
      <ZeitMassActionToolbar
        selectedIds={Array.from(selectedIds)}
        onClearSelection={() => setSelectedIds(new Set())}
        onAfterAction={() => { setSelectedIds(new Set()); load(); }}
        canApprove={canApprove}
        canEdit={canEdit}
        canDelete={canDelete}
      />
    </div>
  );
}

// ============================================================
// BreakEditor — visual LP/RP/SO editor with Quick-Add buttons,
// per-type sub-tables, live Lenkzeit calculation.
//
// Types:
//   RP = Ruhepause (subtracted from Lenkzeit)
//   LP = Lenkpause (NOT subtracted from Lenkzeit — only shown)
//   SO = Sonstige Arbeitszeit (subtracted from Lenkzeit)
//
//   Lenkzeit = (work_end − work_start) − Σ(RP) − Σ(SO)
// ============================================================
function _hmToMin(t) {
  if (!t || !t.includes(":")) return 0;
  const [h, m] = t.split(":").map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}
function _fmtHm(min) {
  const m = Math.max(0, min | 0);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}
function _diffMin(a, b) { return Math.max(0, _hmToMin(b) - _hmToMin(a)); }

function _minToHm(m) {
  const safe = ((m % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
}
function _randInt(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
function _randFloat(min, max) { return Math.random() * (max - min) + min; }

const TYPE_META = {
  RP: { code: "RP", label: "Ruhepause", color: "rose", hint: "In Arbeitszeit, unbezahlt · ZIEHT von Lenkzeit ab" },
  LP: { code: "LP", label: "Lenkpause", color: "blue", hint: "Nicht in Arbeitszeit · zieht NICHT von Lenkzeit ab" },
  SO: { code: "SAZ", label: "Sonstige Arbeitszeit", color: "amber", hint: "In Arbeitszeit, bezahlt, KEINE Diäten · ZIEHT von Lenkzeit ab" },
};
const COLOR_CLASSES = {
  rose: { btn: "bg-rose-600 hover:bg-rose-700", border: "border-rose-500/30", bg: "bg-rose-500/5", text: "text-rose-300" },
  blue: { btn: "bg-blue-600 hover:bg-blue-700", border: "border-blue-500/30", bg: "bg-blue-500/5", text: "text-blue-300" },
  amber: { btn: "bg-amber-600 hover:bg-amber-700", border: "border-amber-500/30", bg: "bg-amber-500/5", text: "text-amber-300" },
};

function BreakEditor({ breaks, onChange, workStart, workEnd }) {
  const [templates, setTemplates] = useState([]);
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [tplName, setTplName] = useState("");

  // Lazy-load templates on first mount + auto-seed defaults if empty
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let { data } = await api.get("/break-templates");
        if (data.length === 0) {
          await api.post("/break-templates/seed-defaults");
          ({ data } = await api.get("/break-templates"));
        }
        if (!cancelled) setTemplates(data);
      } catch (err) { console.warn("break-templates load failed:", err); /* silent — not blocking */ }
    })();
    return () => { cancelled = true; };
  }, []);

  function add(type) { onChange([...breaks, { start: "", end: "", type }]); }
  function update(i, patch) {
    onChange(breaks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  }
  function remove(i) { onChange(breaks.filter((_, idx) => idx !== i)); }

  function applyTemplate(tplId) {
    if (!tplId) return;
    const tpl = templates.find((t) => t.id === tplId);
    if (!tpl) return;

    // Phase 5Z.2 — auto-placement: compute random offset/duration per entry
    const wsBase = workStart || "06:00";
    const wsMin = _hmToMin(wsBase);
    const computed = tpl.entries.map((e) => {
      if (e.auto) {
        const offsetH = _randFloat(
          typeof e.min_offset_h === "number" ? e.min_offset_h : 4,
          typeof e.max_offset_h === "number" ? e.max_offset_h : 6,
        );
        const durMin = _randInt(
          typeof e.min_dur_min === "number" ? e.min_dur_min : 30,
          typeof e.max_dur_min === "number" ? e.max_dur_min : 30,
        );
        // Snap start to 5-minute grid for tidy times
        const rawStart = wsMin + Math.round(offsetH * 60);
        const startMin = Math.round(rawStart / 5) * 5;
        const endMin = startMin + durMin;
        return { type: e.type || "RP", start: _minToHm(startMin), end: _minToHm(endMin) };
      }
      return { type: e.type || "RP", start: e.start || "", end: e.end || "" };
    });
    onChange(computed);
    const autoCount = tpl.entries.filter((e) => e.auto).length;
    toast.success(
      autoCount > 0
        ? `Vorlage „${tpl.name}" angewendet (${tpl.entries.length} Pause${tpl.entries.length === 1 ? "" : "n"} · zufällig platziert${workStart ? "" : ", basiert auf 06:00"})`
        : `Vorlage „${tpl.name}" angewendet (${tpl.entries.length} Pausen)`,
    );
  }

  async function saveAsTemplate() {
    const name = tplName.trim();
    if (!name) { toast.error("Bitte Namen eingeben"); return; }
    try {
      const { data } = await api.post("/break-templates", {
        name, scope: "global",
        entries: breaks.map((b) => ({ type: b.type || "RP", start: b.start || "", end: b.end || "" })),
      });
      setTemplates((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
      setShowSaveAs(false); setTplName("");
      toast.success(`Vorlage „${name}" gespeichert`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  }

  async function deleteTemplate(tplId, name) {
    if (!confirm(`Vorlage „${name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/break-templates/${tplId}`);
      setTemplates((p) => p.filter((t) => t.id !== tplId));
      toast.success("Vorlage gelöscht");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  }

  const einsatzMin = workStart && workEnd ? _diffMin(workStart, workEnd) : 0;
  const sumByType = { RP: 0, LP: 0, SO: 0 };
  breaks.forEach((b) => {
    if (b.start && b.end) sumByType[b.type || "RP"] += _diffMin(b.start, b.end);
  });
  const lenkzeitMin = Math.max(0, einsatzMin - sumByType.RP - sumByType.SO);

  return (
    <div className="space-y-3" data-testid="break-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
          Pausen / Unterbrechungen (LP · RP · SO)
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Template picker */}
          <div className="relative inline-flex items-center gap-1">
            <select
              onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }}
              data-testid="break-template-picker"
              className="text-[10px] bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-100 hover:border-zinc-500"
              defaultValue=""
            >
              <option value="">📋 Vorlage anwenden…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {templates.length > 0 && (
              <button type="button"
                onClick={() => {
                  const tpl = templates[templates.length - 1];
                  if (tpl) deleteTemplate(tpl.id, tpl.name);
                }}
                title="Letzte Vorlage löschen"
                className="hidden"
              />
            )}
          </div>
          <button type="button" onClick={() => setShowSaveAs((s) => !s)}
            data-testid="break-template-save-toggle"
            className="text-[10px] font-semibold px-2.5 py-1 rounded text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700">
            💾 Als Vorlage speichern
          </button>
          <span className="w-px h-5 bg-zinc-700 mx-0.5" />
          {Object.entries(TYPE_META).map(([k, m]) => (
            <button key={k} type="button" onClick={() => add(k)}
              data-testid={`break-quickadd-${k.toLowerCase()}`}
              title={m.hint}
              className={`text-[10px] font-semibold px-2.5 py-1 rounded text-white ${COLOR_CLASSES[m.color].btn}`}>
              + {m.code} {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Save-as inline panel */}
      {showSaveAs && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded p-3 flex items-center gap-2" data-testid="break-template-save-panel">
          <input type="text" value={tplName} onChange={(e) => setTplName(e.target.value)}
            placeholder='Name z. B. „Standard-Tour mit 30 min Pause"'
            data-testid="break-template-name-input"
            className="flex-1 px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-sm text-zinc-100" />
          <button type="button" onClick={saveAsTemplate} data-testid="break-template-save-btn"
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold">Speichern</button>
          <button type="button" onClick={() => { setShowSaveAs(false); setTplName(""); }}
            className="px-3 py-1.5 text-zinc-400 hover:text-zinc-100 text-xs">Abbrechen</button>
        </div>
      )}

      {/* Templates list (compact, with delete) */}
      {templates.length > 0 && (
        <details className="group" data-testid="break-templates-list">
          <summary className="cursor-pointer text-[10px] text-zinc-500 hover:text-zinc-300">
            ▸ {templates.length} verfügbare Vorlagen anzeigen
          </summary>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs">
                <button type="button" onClick={() => applyTemplate(t.id)}
                  className="flex-1 text-left text-zinc-200 hover:text-amber-300 truncate"
                  data-testid={`break-template-apply-${t.id}`}
                  title={t.description || ""}>
                  {t.name} <span className="text-zinc-500">· {t.entries.length}</span>
                </button>
                <button type="button" onClick={() => deleteTemplate(t.id, t.name)}
                  data-testid={`break-template-delete-${t.id}`}
                  className="text-zinc-500 hover:text-red-400 ml-1" title="Löschen">×</button>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Live Lenkzeit summary */}
      <div className="grid grid-cols-4 gap-2 bg-zinc-950 border border-zinc-800 rounded p-3" data-testid="break-summary">
        <Stat label="Einsatzzeit" value={_fmtHm(einsatzMin)} hint={`${workStart || "—"} → ${workEnd || "—"}`} />
        <Stat label="− Σ RP" value={_fmtHm(sumByType.RP)} color="rose" />
        <Stat label="− Σ SAZ" value={_fmtHm(sumByType.SO)} color="amber" />
        <Stat label="= Lenkzeit" value={_fmtHm(lenkzeitMin)} color="emerald" emphasis />
      </div>

      {/* Per-type groups */}
      {(["LP", "RP", "SO"]).map((t) => (
        <BreakTypeGroup key={t}
          type={t}
          breaks={breaks}
          update={update}
          remove={remove}
          add={add}
        />
      ))}

      {breaks.length === 0 && (
        <div className="text-[11px] text-zinc-500 italic bg-zinc-950 border border-zinc-800 border-dashed rounded p-3 text-center">
          Noch keine Pausen eingetragen. Klicke oben „+ LP", „+ RP" oder „+ SAZ" um eine Pause hinzuzufügen — oder wende eine Vorlage an.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint, color, emphasis }) {
  const txt = color === "rose" ? "text-rose-300"
            : color === "amber" ? "text-amber-300"
            : color === "emerald" ? "text-emerald-300"
            : "text-zinc-100";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`font-mono tabular-nums ${emphasis ? "text-lg font-bold" : "text-base"} ${txt}`}>{value}</div>
      {hint && <div className="text-[9px] text-zinc-600 mt-0.5">{hint}</div>}
    </div>
  );
}

// Phase 5Z.33 — Status-Summary-Strip + Quick-Filter-Pills sub-components
function StatusPill({ label, count, color = "zinc", active, onClick, testid }) {
  const colorMap = {
    zinc:    { active: "bg-zinc-700 text-zinc-100 border-zinc-600",    idle: "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-700" },
    amber:   { active: "bg-amber-500/20 text-amber-200 border-amber-500/50", idle: "bg-zinc-900 text-amber-300 border-zinc-800 hover:border-amber-500/40" },
    blue:    { active: "bg-blue-500/20 text-blue-200 border-blue-500/50",    idle: "bg-zinc-900 text-blue-300 border-zinc-800 hover:border-blue-500/40" },
    emerald: { active: "bg-emerald-500/20 text-emerald-200 border-emerald-500/50", idle: "bg-zinc-900 text-emerald-300 border-zinc-800 hover:border-emerald-500/40" },
    rose:    { active: "bg-rose-500/20 text-rose-200 border-rose-500/50",    idle: "bg-zinc-900 text-rose-300 border-zinc-800 hover:border-rose-500/40" },
  }[color];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border transition-colors ${active ? colorMap.active : colorMap.idle}`}
    >
      <span className="font-mono tabular-nums font-semibold">{count}</span>
      <span>{label}</span>
    </button>
  );
}

function PillBtn({ icon: Icon, label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border transition-colors ${
        active
          ? "bg-violet-500/15 text-violet-200 border-violet-500/40"
          : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
      }`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}

function BreakTypeGroup({ type, breaks, update, remove, add }) {
  const meta = TYPE_META[type];
  const cc = COLOR_CLASSES[meta.color];
  const items = breaks
    .map((b, idx) => ({ b, idx }))
    .filter(({ b }) => (b.type || "RP") === type);
  if (items.length === 0) return null;
  const total = items.reduce((a, { b }) => a + (b.start && b.end ? _diffMin(b.start, b.end) : 0), 0);
  return (
    <div className={`border ${cc.border} ${cc.bg} rounded p-3`} data-testid={`break-group-${type.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-xs font-semibold ${cc.text} flex items-center gap-2`}>
          <span className="inline-flex items-center justify-center w-6 h-6 rounded font-bold text-white text-[10px] bg-current opacity-80">
            <span className="text-zinc-900">{meta.code}</span>
          </span>
          {meta.label}
          <span className="text-[10px] text-zinc-500 font-normal">· {meta.hint}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-zinc-300">Σ {_fmtHm(total)}</span>
          <button type="button" onClick={() => add(type)} data-testid={`break-add-${type.toLowerCase()}`}
            className="text-[10px] text-zinc-400 hover:text-zinc-100">+ Weitere {meta.code}</button>
        </div>
      </div>
      <div className="space-y-1.5">
        {items.map(({ b, idx }) => (
          <div key={idx} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center" data-testid={`break-row-${idx}`}>
            <input type="time" value={b.start || ""} onChange={(e) => update(idx, { start: e.target.value })}
              data-testid={`break-start-${idx}`}
              className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100" />
            <input type="time" value={b.end || ""} onChange={(e) => update(idx, { end: e.target.value })}
              data-testid={`break-end-${idx}`}
              className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100" />
            <span className="text-xs font-mono text-zinc-300 min-w-[55px] text-right">
              {b.start && b.end ? _fmtHm(_diffMin(b.start, b.end)) : "—"}
            </span>
            <button type="button" onClick={() => remove(idx)} data-testid={`break-remove-${idx}`}
              className="text-red-400 hover:text-red-300 p-1" title="Löschen">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}



// ─── Tagesansicht (Single-day picker with prev/next-day arrows) ─────────────
//     One click sets both date_from and date_to to the same day, giving a
//     clean "what happened on this exact day" view. Backed by a native
//     <input type="date"> so keyboard navigation + AT locale Just Works.
function DayPicker({ dateFrom, dateTo, setDateFrom, setDateTo }) {
  function isoLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }
  // We're in single-day mode when both dates are set AND equal
  const isDayMode = dateFrom && dateTo && dateFrom === dateTo;
  const todayIso = isoLocal(new Date());

  function setToDay(d) {
    const iso = typeof d === "string" ? d : isoLocal(d);
    setDateFrom(iso);
    setDateTo(iso);
  }
  function shift(days) {
    const base = isDayMode ? dateFrom : todayIso;
    const [y, m, dd] = base.split("-").map(Number);
    const d = new Date(y, m - 1, dd);
    d.setDate(d.getDate() + days);
    setToDay(d);
  }

  // Friendly label
  function fmt(iso) {
    if (!iso) return "";
    const [y, m, dd] = iso.split("-").map(Number);
    const d = new Date(y, m - 1, dd);
    return d.toLocaleDateString("de-AT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  }
  const label = isDayMode ? fmt(dateFrom) : "Tagesansicht";

  return (
    <div className="inline-flex items-stretch gap-0.5 rounded border border-zinc-800 overflow-hidden"
         data-testid="zeit-day-picker">
      <button
        type="button"
        onClick={() => shift(-1)}
        title="Vortag"
        data-testid="zeit-day-prev"
        className={`px-2 ${isDayMode ? "bg-zinc-900 hover:bg-zinc-800 text-zinc-200" : "bg-zinc-950 text-zinc-600 hover:text-zinc-300"} transition-colors`}
      >
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>
      <label className={`inline-flex items-center gap-1.5 px-2.5 cursor-pointer ${isDayMode ? "bg-amber-500/15 text-amber-200" : "bg-zinc-950 text-zinc-400 hover:text-zinc-200"} transition-colors`}>
        <Calendar className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">{label}</span>
        <input
          type="date"
          value={isDayMode ? dateFrom : ""}
          onChange={(e) => { if (e.target.value) setToDay(e.target.value); }}
          data-testid="zeit-day-picker-input"
          className="absolute opacity-0 pointer-events-none w-0 h-0"
        />
        {/* Visible click target overlays */}
        <input
          type="date"
          value={isDayMode ? dateFrom : ""}
          onChange={(e) => { if (e.target.value) setToDay(e.target.value); }}
          aria-label="Tagesansicht-Datum"
          data-testid="zeit-day-picker-native"
          className="bg-transparent text-transparent w-6 -ml-1 cursor-pointer outline-none"
          style={{ colorScheme: "dark" }}
        />
      </label>
      <button
        type="button"
        onClick={() => shift(1)}
        title="Folgetag"
        data-testid="zeit-day-next"
        className={`px-2 ${isDayMode ? "bg-zinc-900 hover:bg-zinc-800 text-zinc-200" : "bg-zinc-950 text-zinc-600 hover:text-zinc-300"} transition-colors`}
      >
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setToDay(todayIso)}
        title="Heute"
        data-testid="zeit-day-today"
        className={`px-2 text-[10px] uppercase tracking-wider font-semibold ${isDayMode && dateFrom === todayIso ? "bg-amber-500 text-zinc-950" : "bg-zinc-950 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"} transition-colors`}
      >
        Heute
      </button>
    </div>
  );
}

// ─── Quick date presets — Heute / Gestern / Diese Woche / Letzte Woche /
//     Dieser Monat / Letzter Monat / Letzte 30 Tage. Bookends populate
//     both date-from + date-to in one click for fast historical filtering.
function DatePresetBar({ setDateFrom, setDateTo }) {
  function isoLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }
  function apply(from, to) {
    setDateFrom(isoLocal(from));
    setDateTo(isoLocal(to));
  }
  function clear() { setDateFrom(""); setDateTo(""); }

  function presets() {
    const today = new Date(); today.setHours(0,0,0,0);
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    // Week starts Monday in AT
    const dow = (today.getDay() + 6) % 7; // 0=Mo .. 6=So
    const weekStart = new Date(today); weekStart.setDate(today.getDate() - dow);
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
    const lastWeekStart = new Date(weekStart); lastWeekStart.setDate(weekStart.getDate() - 7);
    const lastWeekEnd = new Date(weekStart); lastWeekEnd.setDate(weekStart.getDate() - 1);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    const last30Start = new Date(today); last30Start.setDate(today.getDate() - 29);
    return [
      { id: "heute", label: "Heute", from: today, to: today },
      { id: "gestern", label: "Gestern", from: yesterday, to: yesterday },
      { id: "diese-woche", label: "Diese Woche", from: weekStart, to: weekEnd },
      { id: "letzte-woche", label: "Letzte Woche", from: lastWeekStart, to: lastWeekEnd },
      { id: "dieser-monat", label: "Dieser Monat", from: monthStart, to: monthEnd },
      { id: "letzter-monat", label: "Letzter Monat", from: lastMonthStart, to: lastMonthEnd },
      { id: "letzte-30", label: "Letzte 30 Tage", from: last30Start, to: today },
    ];
  }

  return (
    <div className="inline-flex items-center gap-1 flex-wrap" data-testid="date-presets">
      {presets().map(p => (
        <button
          key={p.id}
          type="button"
          onClick={() => apply(p.from, p.to)}
          data-testid={`date-preset-${p.id}`}
          className="px-2.5 py-1.5 text-xs bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-blue-500/40 text-zinc-300 rounded transition-colors"
        >
          {p.label}
        </button>
      ))}
      <button
        type="button"
        onClick={clear}
        data-testid="date-preset-clear"
        className="px-2.5 py-1.5 text-xs bg-transparent hover:bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-zinc-300 rounded transition-colors"
        title="Datumsfilter zurücksetzen"
      >
        ×
      </button>
    </div>
  );
}
